import { Subscription } from 'rxjs';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ReplaceMap } from 'src/lib/fliko/interfaces/replaceMap';
import { AllPermissions } from 'src/lib/fliko/models/company/employeeRole.data';
import { DEFAULT_OFFER_FEATURE, getOfferFeatureWithDefaults } from 'src/lib/fliko/models/defaults/offer.defaults';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { VisualHelper } from 'src/app/shared/helpers/visual.helper';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ContextType } from 'src/app/view/user-context/services/models/context.data';
import { RWDService } from 'src/app/view/user-context/services/rwd.service';

export interface NavigationVM {
  id: string;
  icon: string;
  showCond: boolean;
  titleKey: string;
  titleReplaceMap?: ReplaceMap;
  navPath: string;
}

const DEFAULT_OFFER_TITLE_REPLACE_MAP: ReplaceMap = {
  $offerIdentification: DEFAULT_OFFER_FEATURE.identificationStyle
};

@Component({
  selector: 'app-navigation[contextType]',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit, OnDestroy {
  @Input() contextType: ContextType = ContextType.ESTATE;

  public logo?: string;
  public logoBgColor: string = 'unset';
  public closeColor: string = 'white';
  public selected: string = '';

  // Navigation tabs
  // prettier-ignore
  public estateNav: NavigationVM[] = [
    /* eslint-disable max-len */
    { id: 'dash',         icon: 'dashboard',            navPath: 'dashboard',                titleKey: 'navigation.dashboard',   showCond: true  },
    { id: 'mess',         icon: 'question_answer',      navPath: 'messages/active',          titleKey: 'navigation.chat',        showCond: false },
    { id: 'news',         icon: 'event_note',           navPath: 'news',                     titleKey: 'navigation.news',        showCond: false },
    { id: 'defect',       icon: 'build',                navPath: 'defects',                  titleKey: 'navigation.defects',     showCond: false },
    { id: 'concierge',    icon: 'room_service',         navPath: 'concierge',                titleKey: 'navigation.concierge',   showCond: false },
    { id: 'res',          icon: 'assignment_turned_in', navPath: 'resolutions',              titleKey: 'navigation.resolutions', showCond: false },
    { id: 'poll',         icon: 'view_list',            navPath: 'polls',                    titleKey: 'navigation.polls',       showCond: false },
    { id: 'book',         icon: 'import_contacts',      navPath: 'booking/calendar',         titleKey: 'navigation.booking',     showCond: false },
    { id: 'reck',         icon: 'attach_money',         navPath: 'reckonings',               titleKey: 'navigation.reckonings',  showCond: false },
    { id: 'userComm',     icon: 'people',               navPath: 'users/landlords',          titleKey: 'navigation.residents',   showCond: false },
    { id: 'userPRS',      icon: 'people',               navPath: 'users/agreements',         titleKey: 'navigation.agreements',  showCond: false },
    { id: 'connections',  icon: 'call',                 navPath: 'connections',              titleKey: 'navigation.connections', showCond: false },
    { id: 'offers',       icon: 'local_offer',          navPath: 'offers',                   titleKey: 'navigation.offers',      titleReplaceMap: DEFAULT_OFFER_TITLE_REPLACE_MAP, showCond: false },
    { id: 'estate',       icon: 'location_city',        navPath: 'estate/details',           titleKey: 'navigation.estate',      showCond: true  },
    { id: 'cale',         icon: 'calendar_today',       navPath: 'calendar',                 titleKey: 'navigation.calendar',    showCond: true  },
    { id: 'repo',         icon: 'summarize',            navPath: 'reports',                  titleKey: 'navigation.reports',     showCond: false },
    { id: 'bluebolt',     icon: 'lock_person',          navPath: 'bluebolt/access-profiles', titleKey: 'navigation.bluebolt',    showCond: false },
    { id: 'web',          icon: 'widgets',              navPath: 'websites',                 titleKey: 'navigation.websites',    showCond: false },
    { id: 'settings',     icon: 'settings',             navPath: 'settings',                 titleKey: 'label.settings',         showCond: false }
    /* eslint-enable max-len */
  ];
  // prettier-ignore
  public userNav: NavigationVM[] = [
    { id: 'help',   icon: 'help',     navPath: 'help',          titleKey: 'navigation.help',      showCond: true  },
    { id: 'event',  icon: 'history',  navPath: 'event-log',     titleKey: 'navigation.eventLog',  showCond: true  },
    { id: 'admin',  icon: 'settings', navPath: 'admin/estate',  titleKey: 'Administracja',        showCond: false }
  ];

  // prettier-ignore
  public companyNav: NavigationVM[] = [
    /* eslint-disable max-len */
    { id: 'dash',      icon: 'dashboard',          navPath: 'company/dashboard',      titleKey: 'navigation.dashboard',       showCond: true },
  // { id: 'estate', icon: 'location_city', navPath: 'company/estates', titleKey: 'navigation.compEstates', showCond: true },
    { id: 'access',    icon: 'people',             navPath: 'company/employees',      titleKey: 'navigation.employees',       showCond: true },
    { id: 'logins',    icon: 'login',              navPath: 'company/logins',         titleKey: 'navigation.logins',          showCond: true },
    { id: 'sms',       icon: 'mail',               navPath: 'company/sms',            titleKey: 'navigation.sms',             showCond: true },
    { id: 'contract',  icon: 'construction',       navPath: 'company/subcontractors', titleKey: 'navigation.subcontractors',  showCond: true },
    { id: 'review',    icon: 'check',              navPath: 'company/reviews',        titleKey: 'navigation.reviews',         showCond: true },
    { id: 'insurances', icon: 'shield_with_heart', navPath: 'company/insurances',     titleKey: 'navigation.insurances',      showCond: true },
    { id: 'offers',    icon: 'local_offer',        navPath: 'offers',                 titleKey: 'navigation.offers',          titleReplaceMap: DEFAULT_OFFER_TITLE_REPLACE_MAP, showCond: false },
    { id: 'cale',      icon: 'calendar_today',     navPath: 'company/calendar',       titleKey: 'navigation.calendar',        showCond: true },
    { id: 'event',     icon: 'history',            navPath: 'company/event-log',      titleKey: 'navigation.eventLog',        showCond: true },
    { id: 'estConf',   icon: 'settings',           navPath: 'company/config',         titleKey: 'navigation.e_settings',      showCond: true }
    /* eslint-enable max-len */
  ];

  private subscription$ = new Subscription();
  private latestNavigationId = 0;

  CT = ContextType;

  constructor(
    public rwdService: RWDService,
    private ctxDS: ContextDataService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialize selected synchronously at the start to avoid NG0100 error
    this.selected = this.getSelected(this.router.url);

    this.subscription$.add(
      this.ctxDS.customTheme$.subscribe((theme) => {
        this.logo = theme?.logo.url ?? undefined;
        const color = theme?.logo.background;
        this.logoBgColor = color ?? 'unset';
        this.closeColor = color ? VisualHelper.textColorBg(color) : 'white';
      })
    );

    this.subscription$.add(
      this.router.events.subscribe((event) => {
        let url: string;
        if (event instanceof NavigationStart) {
          url = event.url;
        } else if (event instanceof NavigationEnd) {
          url = event.urlAfterRedirects;
        } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
          url = this.router.url;
        } else {
          return;
        }

        if (event.id < this.latestNavigationId) return;
        this.latestNavigationId = event.id;
        this.selected = this.getSelected(url);
        this.cdr.detectChanges();
      })
    );

    this.subscription$.add(
      this.ctxDS.contextPerms$.subscribe((perms) => {
        if (!perms) {
          this.estateNav.forEach((nav) => (nav.showCond = nav.id === 'dash'));
          this.companyNav.forEach((nav) => (nav.showCond = false));
          this.userNav.forEach((nav) => (nav.showCond = nav.id === 'help'));
          return;
        }

        const isSuperAdmin = perms.superAdmin;
        const comm = !perms.isPrs;
        const web = perms.feat?.web;
        const both = perms.feat?.both;
        const permissions = perms.ePerm;
        const offerTitleReplaceMap: ReplaceMap = {
          $offerIdentification: getOfferFeatureWithDefaults(both?.offer).identificationStyle
        };

        const perm = (code: keyof AllPermissions) => {
          return isSuperAdmin || (permissions ? !!ObjHlp.get<any, string>(permissions[code], 'view') : false);
        };

        // prettier-ignore
        this.estateNav.forEach((nav) => {
          switch (nav.id) {
              case 'dash':        nav.showCond = true; return;
              case 'cale':        nav.showCond = perm('calendar');                                                         return;
              case 'mess':        nav.showCond = perms.canChat === true &&          (web?.messaging ?? true);              return;
              case 'news':        nav.showCond = perm('news')           &&          (web?.news ?? true);                   return;
              case 'defect':      nav.showCond = perm('defects')        &&          (web?.defect?.enabled ?? true);        return;
              case 'concierge':   nav.showCond = perm('concierge')      &&          (web?.concierge?.enabled ?? false);    return;
              case 'res':         nav.showCond = perm('resolutions')    &&  comm && (web?.resolution ?? true);             return;
              case 'poll':        nav.showCond = perm('polls')          &&          (web?.poll ?? true);                   return;
              case 'userComm':    nav.showCond = perm('user')           &&  comm && (web?.residents ?? true);              return;
              case 'userPRS':     nav.showCond = perm('user')           && !comm && (web?.residents ?? true);              return;
              case 'connections': nav.showCond = perm('user')           &&          (web?.connections?.enabled ?? false);  return;
              case 'estate':      nav.showCond = perm('e_data')         &&          (web?.estateDetails?.enabled ?? true); return;
              case 'book':        nav.showCond = perm('booking')        &&          (web?.booking ?? true);                return;
              case 'reck':        nav.showCond = perm('reckonings')     &&          (web?.reckoning?.enabled ?? true);     return;
              case 'repo':        nav.showCond = perm('reports')        &&         !!web?.report?.enabled;                 return;
              case 'bluebolt':    nav.showCond = perm('bluebolt')       &&         !!web?.bluebolt;                        return;
              case 'web':         nav.showCond = perm('websites')       &&          (web?.websites ?? true);               return;
              case 'settings':    nav.showCond = perm('e_settings');                                                     return;
              case 'offers':
                nav.titleReplaceMap = offerTitleReplaceMap;
                nav.showCond = perm('offers') && (both?.offer?.enabled ?? false);
                return;
            }
        });

        // prettier-ignore
        this.userNav.forEach((nav) => {
          switch (nav.id) {
            case 'event': nav.showCond = perm('log') && (web?.eventLog ?? true); break;
            case 'admin': nav.showCond = isSuperAdmin;                           break;
          }
        });

        // prettier-ignore
        this.companyNav.forEach((nav) => {
          switch (nav.id) {
            case 'dash':      nav.showCond = perm('c_dashboard'); return;
            case 'access':
            case 'logins':    nav.showCond = perm('c_role');      return;
            case 'sms':
            case 'review':
            case 'insurances':
            case 'contract':  nav.showCond = perm('c_report');    return;
            case 'event':     nav.showCond = perm('c_log');       return;
            case 'cale':      nav.showCond = perm('c_calendar');  return;
            case 'estConf':   nav.showCond = perm('c_settings');  return;
            case 'offers':
              nav.titleReplaceMap = offerTitleReplaceMap;
              nav.showCond = perm('offers') && (both?.offer?.enabled ?? false);
              return;
          }
        });
      })
    );

    AsyncHlp.inNextTick(() => {
      if (this.latestNavigationId === 0) this.selected = this.getSelected(this.router.url);
    });
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private getSelected(url: string): string {
    const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
    return segments[0] === 'company' ? (segments[1] ?? '') : (segments[0] ?? '');
  }

  public isSelected(vm: NavigationVM): boolean {
    return this.selected === this.getSelected(vm.navPath);
  }
}
